import { apiClient as api } from "./httpClient";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_COUNT,
  CATEGORY_SUGGESTIONS,
  downloadBlob,
  formatFileSize,
} from "./workflowsService";

// Documentos do Playbook: material de consulta da equipe (manual, política, modelo, vídeo).
// É o irmão simples do fluxo de trabalho — mesmo cadastro e mesma consulta, sem etapas e sem
// versão: o que vale é o arquivo atual.
// Os limites de arquivo, as sugestões de área e os utilitários de download são os MESMOS dos
// fluxos e vêm de lá reexportados: duplicar faria os dois módulos divergirem em silêncio.

export {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_COUNT,
  CATEGORY_SUGGESTIONS,
  downloadBlob,
  formatFileSize,
};

export interface PlaybookDocumentAttachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface PlaybookDocumentListItem {
  id: string;
  title: string;
  summary: string | null;
  category: string | null;
  /** Guarda-chuva ao qual o documento pertence ("Integração de novos terapeutas"). */
  groupName: string | null;
  videoUrl: string | null;
  active: boolean;
  attachmentCount: number;
  createdAt: string;
  updatedAt: string | null;
  visibilityProfileIds: string[];
}

/** Perfil do tenant, para o campo "quem pode ver". */
export interface PlaybookDocumentProfileOption {
  id: string;
  name: string;
}

export interface SavePlaybookDocumentPayload {
  title: string;
  summary: string | null;
  category: string | null;
  groupName: string | null;
  videoUrl: string | null;
  /** Pelo menos um: documento sem perfil não aparece para ninguém, e a API recusa. */
  visibilityProfileIds: string[];
}

export const playbookDocumentsService = {
  getByEntity: async (
    entityId: string,
    includeInactive = true
  ): Promise<PlaybookDocumentListItem[]> => {
    const response = await api.get(
      `/playbook-documents/by-entity/${entityId}?includeInactive=${includeInactive}`
    );
    return response.data;
  },

  getProfiles: async (): Promise<PlaybookDocumentProfileOption[]> => {
    const response = await api.get("/playbook-documents/profiles");
    return response.data;
  },

  create: async (entityId: string, payload: SavePlaybookDocumentPayload): Promise<string> => {
    const response = await api.post("/playbook-documents", { entityId, ...payload });
    return response.data.id;
  },

  update: async (documentId: string, payload: SavePlaybookDocumentPayload): Promise<void> => {
    await api.put(`/playbook-documents/${documentId}`, payload);
  },

  setActive: async (documentId: string, active: boolean): Promise<void> => {
    await api.patch(`/playbook-documents/${documentId}/active`, { active });
  },

  remove: async (documentId: string): Promise<void> => {
    await api.delete(`/playbook-documents/${documentId}`);
  },

  getAttachments: async (documentId: string): Promise<PlaybookDocumentAttachment[]> => {
    const response = await api.get(`/playbook-documents/${documentId}/attachments`);
    return response.data;
  },

  // O apiClient manda Content-Type: application/json por padrão e ele VAZA para o FormData
  // (a API responde 415). Anulando o header, o browser define o multipart/form-data com boundary.
  uploadAttachment: async (documentId: string, file: File): Promise<string> => {
    const form = new FormData();
    form.append("file", file);
    const response = await api.post(`/playbook-documents/${documentId}/attachments`, form, {
      headers: { "Content-Type": null },
    });
    return response.data.id as string;
  },

  removeAttachment: async (attachmentId: string): Promise<void> => {
    await api.delete(`/playbook-documents/attachments/${attachmentId}`);
  },
};

// ===== Consulta (Playbook) — permissão document-library =====

export type DocumentAccessReason = "visibility" | "admin";

export const DOCUMENT_ACCESS_REASON_LABELS: Record<DocumentAccessReason, string> = {
  visibility: "Liberado para o seu perfil",
  admin: "Visível por ser administrador da entidade",
};

export interface DocumentLibraryItem {
  id: string;
  title: string;
  summary: string | null;
  category: string | null;
  groupName: string | null;
  hasVideo: boolean;
  attachmentCount: number;
  updatedAt: string | null;
  accessReason: DocumentAccessReason;
}

export interface DocumentLibraryPage {
  items: DocumentLibraryItem[];
  categories: string[];
  groups: string[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface DocumentLibraryDetail {
  id: string;
  title: string;
  summary: string | null;
  category: string | null;
  groupName: string | null;
  /** URL já normalizada para embed pela API; null quando o documento não tem vídeo. */
  videoUrl: string | null;
  createdAt: string;
  updatedAt: string | null;
  attachments: PlaybookDocumentAttachment[];
}

export interface DocumentLibraryFilters {
  search?: string;
  category?: string;
  groupName?: string;
  pageNumber: number;
  pageSize: number;
}

export const documentLibraryService = {
  getByEntity: async (
    entityId: string,
    filters: DocumentLibraryFilters
  ): Promise<DocumentLibraryPage> => {
    const response = await api.get(`/document-library/by-entity/${entityId}`, {
      params: {
        search: filters.search?.trim() || undefined,
        category: filters.category || undefined,
        groupName: filters.groupName || undefined,
        pageNumber: filters.pageNumber,
        pageSize: filters.pageSize,
      },
    });
    return response.data;
  },

  getById: async (documentId: string): Promise<DocumentLibraryDetail> => {
    const response = await api.get(`/document-library/${documentId}`);
    return response.data;
  },

  downloadAttachment: async (attachmentId: string): Promise<Blob> => {
    const response = await api.get(`/document-library/attachments/${attachmentId}/file`, {
      responseType: "blob",
    });
    return response.data;
  },
};
